import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, map, Observable } from 'rxjs';
import { ChargeItem, ChargeItemDefinition, Invoice, ServiceRequest, SupplyDelivery } from 'fhir/r4';
import { backendEndPointToken } from '../../app.config';
import { extractItemsRequiredRows, ItemsRequiredRow } from '../activity-definition-items-required';
import { UtilityService } from '../utility.service';
import { AuthService } from '../auth/auth.service';
import { LabBillingPlaceholderService } from './lab-billing-placeholder.service';
import { ServiceRequestBillingPlaceholderService } from './service-request-billing-placeholder.service';
import { inferServiceRequestType } from '../service-request-type.util';

export type ProcedureFinalizationPreparation = {
  serviceRequest: ServiceRequest;
  supplyDeliveries: SupplyDelivery[];
  invoices: Invoice[];
  itemsRequired: ItemsRequiredRow[];
  totalConsumedCost: number;
  inventoryReconciliations: InventoryCostReconciliation[];
  lowStockAlerts: Array<{ resource: any; notification: any }>;
};

export type InventoryCostReconciliation = {
  material: any;
  itemName: string;
  unit?: string;
  requiredQuantity: number;
  recordedQuantityUsed: number;
  shortageQuantity: number;
  recordedBatchCost: number;
  estimatedUnitCost: number;
  estimatedShortageCost: number;
  pricingBasis: string;
  sourceBatchId?: string;
};

/**
 * Finalization support for Procedure and Observation service requests. Add Report
 * retains its existing flow; this service provides the ActivityDefinition snapshot
 * and inventory-consumption behaviour used by request completion transactions.
 */
@Injectable({ providedIn: 'root' })
export class ProcedureFinalizationInventoryService {
  private readonly http = inject(HttpClient);
  private readonly backend = inject(backendEndPointToken);
  private readonly utility = inject(UtilityService);
  private readonly auth = inject(AuthService);
  private readonly billingPlaceholder = inject(LabBillingPlaceholderService);
  private readonly srBillingPlaceholder = inject(ServiceRequestBillingPlaceholderService);

  async prepare(serviceRequestId: string): Promise<ProcedureFinalizationPreparation> {
    const sr = await firstValueFrom(this.http.get<ServiceRequest>(
      `${this.backend}/ServiceRequest/${encodeURIComponent(serviceRequestId)}`
    ));
    const activityDefinition = await this.resolveActivityDefinition(sr);
    const canonical = this.toCanonical(activityDefinition);
    const updatedSr: ServiceRequest = {
      ...sr,
      status: 'completed',
      ...(canonical && !(sr.instantiatesCanonical || []).length ? { instantiatesCanonical: [canonical] } : {})
    };

    const itemsRequired = extractItemsRequiredRows(activityDefinition);
    const deliveries = await firstValueFrom(this.utility.fetchAll<SupplyDelivery>({
      resourceType: 'SupplyDelivery',
      queries: { status: 'completed' },
      checkOrgWideState: false,
      updateOrgWideState: true,
      maxPages: 100
    }));

    const consumption = this.applyUsage(deliveries, itemsRequired, updatedSr.subject?.reference);
    const invoiceCost = await this.buildPaidInvoiceCostUpdates(updatedSr, consumption.totalCost, consumption.reconciliations);
    return {
      serviceRequest: updatedSr,
      supplyDeliveries: consumption.deliveries,
      invoices: invoiceCost.invoices,
      itemsRequired,
      totalConsumedCost: consumption.totalCost,
      inventoryReconciliations: consumption.reconciliations,
      lowStockAlerts: consumption.alerts
    };
  }

  private async resolveActivityDefinition(sr: ServiceRequest): Promise<any | null> {
    const canonical = (sr.instantiatesCanonical || []).find(Boolean) || '';
    const instanceId = this.idFromCanonical(canonical);
    if (instanceId) {
      try {
        return await firstValueFrom(this.http.get<any>(`${this.backend}/ActivityDefinition/${encodeURIComponent(instanceId)}`));
      } catch {
        // A retired/missing instance should fall back to the current default.
      }
    }

    const code = sr.code?.coding?.[0]?.code || sr.code?.text || '';
    if (!code) return null;
    // Match Add Report's active-default lookup: use the three-tier org-wide
    // query so paging, cache synchronization, and fallback behavior stay
    // consistent. The server query stays status-only; choose the service code
    // locally afterwards.
    const definitions = await firstValueFrom(this.utility.threeTierQueryAndSyncToStateOnce$<any>({
      resourceType: 'ActivityDefinition',
      urlSearchObject: { status: 'active' },
      querySearchObject: [{ key: 'status', value: 'active' }],
      scope: 'orgWide',
      // expectedMinLength: 200,
      tier1FollowNextLinks: true,
      tier1MaxPages: 100,
    }));
    const matches = (definitions || []).filter((definition: any) => {
      const definitionCode = definition?.code?.coding?.[0]?.code || definition?.code?.text || '';
      return definitionCode.toString().trim().toLowerCase() === code.toString().trim().toLowerCase();
    });
    return matches.find((definition: any) =>
      (definition?.topic || []).some((topic: any) =>
        ((topic?.text ?? '').toString().toLowerCase()).includes('items usage computation')
      )
    ) || matches[0] || null;
  }

  private applyUsage(deliveries: SupplyDelivery[], rows: ItemsRequiredRow[], patientReference?: string): { deliveries: SupplyDelivery[]; totalCost: number; reconciliations: InventoryCostReconciliation[]; alerts: Array<{ resource: any; notification: any }> } {
    const cloned = structuredClone((deliveries || []).filter((delivery) => delivery?.status === 'completed')) as SupplyDelivery[];
    const normalize = (value: any) => (value ?? '').toString().trim().toLowerCase();
    const itemKey = (item: any) => {
      const coding = item?.coding?.[0];
      return coding?.system && coding?.code
        ? `code:${normalize(coding.system)}::${normalize(coding.code)}`
        : `text:${normalize(item?.text || coding?.display || coding?.code)}`;
    };
    const required = new Map<string, number>();
    for (const row of rows || []) {
      const quantity = Number(row?.quantity ?? 0);
      const key = itemKey(row?.items);
      if (key && Number.isFinite(quantity) && quantity > 0) required.set(key, (required.get(key) || 0) + quantity);
    }

    const changed: SupplyDelivery[] = [];
    const reconciliations: InventoryCostReconciliation[] = [];
    const alerts: Array<{ resource: any; notification: any }> = [];
    let totalCost = 0;
    for (const [key, requiredQty] of required) {
      let remainingToDeduct = requiredQty;
      let recordedQuantityUsed = 0;
      let recordedBatchCost = 0;
      let matches = cloned.filter((delivery: any) => itemKey(delivery?.suppliedItem?.itemCodeableConcept) === key)
        .sort((a: any, b: any) => this.dateValue(a) - this.dateValue(b));
      // Legacy/default configurations occasionally differ only by coding system.
      // Match by code as Add Report does when an exact system+code match fails.
      if (!matches.length && key.startsWith('code:')) {
        const codeOnly = key.split('::').slice(1).join('::');
        matches = cloned.filter((delivery: any) => {
          const code = normalize(delivery?.suppliedItem?.itemCodeableConcept?.coding?.[0]?.code);
          return code === codeOnly;
        }).sort((a: any, b: any) => this.dateValue(a) - this.dateValue(b));
      }
      for (const delivery of matches) {
        if (remainingToDeduct <= 0) break;
        const remaining = this.getRemaining(delivery);
        if (remaining.value <= 0) continue;
        const used = Math.min(remaining.value, remainingToDeduct);
        const batchCost = this.unitCost(delivery) * used;
        totalCost += batchCost;
        recordedQuantityUsed += used;
        recordedBatchCost += batchCost;
        this.setRemaining(delivery, remaining.value - used, remaining.unit);
        remainingToDeduct -= used;
        changed.push(delivery);
      }
      const newestBatch = matches[matches.length - 1];
      const shortageQuantity = Math.max(0, remainingToDeduct);
      const estimatedUnitCost = shortageQuantity > 0 && newestBatch ? this.unitCost(newestBatch) : 0;
      const estimatedShortageCost = shortageQuantity * estimatedUnitCost;
      const concept: any = newestBatch?.suppliedItem?.itemCodeableConcept || this.findItemForKey(rows, key, itemKey);
      reconciliations.push({
        material: concept,
        itemName: concept?.text || concept?.coding?.[0]?.display || concept?.coding?.[0]?.code || key.replace(/^(code|text):/, ''),
        unit: this.getRemaining(newestBatch).unit,
        requiredQuantity: requiredQty,
        recordedQuantityUsed,
        shortageQuantity,
        recordedBatchCost,
        estimatedUnitCost,
        estimatedShortageCost,
        pricingBasis: shortageQuantity
          ? (newestBatch ? 'Latest matching SupplyDelivery batch' : 'No matching SupplyDelivery batch found')
          : 'Recorded FIFO batches',
        ...(newestBatch?.id ? { sourceBatchId: newestBatch.id } : {})
      });
      const aggregateRemaining = matches.reduce((sum, delivery) => sum + this.getRemaining(delivery).value, 0);
      const referenceBatch = matches[matches.length - 1];
      const config = this.utility.getSupplyLowStockConfig(referenceBatch);
      const organizationId = this.auth.user.getValue()?.organizationId || this.auth.user.getValue()?.hospitalId;
      if (referenceBatch && organizationId && config?.alertsEnabled !== false && this.utility.isBelowLowStockThreshold(aggregateRemaining, config?.threshold)) {
        const concept: any = referenceBatch?.suppliedItem?.itemCodeableConcept;
        const itemName = concept?.text || concept?.coding?.[0]?.display || concept?.coding?.[0]?.code || 'SupplyDelivery';
        const unit = config?.unit || this.getRemaining(referenceBatch).unit || '';
        alerts.push({
          resource: {
            resourceType: 'CommunicationRequest', status: 'active', priority: 'urgent', authoredOn: new Date().toISOString(),
            recipient: [{ reference: `Organization/${organizationId}` }],
            category: [{ coding: [{ system: 'http://terminology.hl7.org/CodeSystem/communication-category', code: 'alert', display: 'Alert' }] }],
            payload: [{ contentString: `Low stock alert: ${itemName} will have ${aggregateRemaining}${unit ? ` ${unit}` : ''} after finalization. Threshold: ${config?.threshold || 0}${unit ? ` ${unit}` : ''}.` }],
            ...(patientReference ? { subject: { reference: patientReference } } : {})
          },
          notification: { itemName, remaining: aggregateRemaining, threshold: config?.threshold || 0, unit, resourceType: 'SupplyDelivery', resourceId: referenceBatch.id }
        });
      }
    }
    return { deliveries: changed, totalCost, reconciliations, alerts };
  }

  private async buildPaidInvoiceCostUpdates(serviceRequest: ServiceRequest, totalConsumedCost: number, reconciliations: InventoryCostReconciliation[]): Promise<{ invoices: Invoice[]; professionalCost: number }> {
    const patientReference = serviceRequest?.subject?.reference || '';
    const patientId = patientReference.split('/').pop() || '';
    if (!patientId || !serviceRequest?.id) return { invoices: [], professionalCost: 0 };

    // Procedure/surgery placeholders link through the Procedure resource; other
    // types (including observation) resolve through the generic ServiceRequest
    // payment resolver, which tries the lab-style resolver first and falls back
    // to direct ChargeItem->ServiceRequest matching.
    const type = inferServiceRequestType(serviceRequest);
    const billing$: Observable<{ chargeItem: ChargeItem | null; linkedInvoices: Invoice[]; hasBalancedInvoice: boolean }> =
      type === 'procedure' || type === 'surgery'
        ? this.billingPlaceholder.resolveBillingAndPlaceholderForProcedure(serviceRequest, patientId)
        : this.srBillingPlaceholder.resolvePaymentForServiceRequest(serviceRequest, patientId).pipe(
            map((resolved) => ({
              chargeItem: (resolved?.chargeItems || [])[0] ?? null,
              linkedInvoices: resolved?.invoices ?? [],
              hasBalancedInvoice: !!resolved?.hasBalancedInvoice
            }))
          );
    const billing = await firstValueFrom(billing$);
    const linkedChargeItems = billing?.chargeItem ? [billing.chargeItem] : [];
    if (!linkedChargeItems.length) return { invoices: [], professionalCost: 0 };

    const definitions = await firstValueFrom(this.utility.fetchAll<ChargeItemDefinition>({
      resourceType: 'ChargeItemDefinition', queries: { status: 'active' }, checkOrgWideState: false, updateOrgWideState: true, maxPages: 100
    }));
    let professionalCost = 0;
    const serviceCode = serviceRequest.code?.coding?.[0]?.code || serviceRequest.code?.text || '';
    for (const chargeItem of linkedChargeItems) {
      const definition = definitions.find((candidate: any) => {
        const target = candidate?.id ? `ChargeItemDefinition/${candidate.id}` : '';
        return (chargeItem.definitionCanonical || []).some((canonical: string) => canonical === candidate?.id || (!!target && canonical.includes(target)));
      }) || definitions.find((candidate: any) => {
        const candidateCode = candidate?.code?.coding?.[0]?.code || candidate?.code?.text || '';
        return candidateCode.toString().trim().toLowerCase() === serviceCode.toString().trim().toLowerCase();
      });
      for (const extension of definition?.extension || []) {
        if (!(extension?.url === 'http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost' || extension?.url?.endsWith('/ChargeItemDefinition-totalCost'))) continue;
        const type = extension?.extension?.find((item: any) => item?.url === 'type')?.valueCode;
        const amount = Number(extension?.extension?.find((item: any) => item?.url === 'amount')?.valueMoney?.value ?? 0);
        if ((type || '').toString().toLowerCase() === 'base' && Number.isFinite(amount)) professionalCost += amount * Number(chargeItem.quantity?.value || 1);
      }
    }

    const invoices = (billing?.linkedInvoices || []).filter((invoice: any) =>
      (invoice?.status || '').toString().toLowerCase() === 'balanced'
    ) as Invoice[];
    const chargeItemReferences = new Set(linkedChargeItems.filter((item) => !!item.id).map((item) => `ChargeItem/${item.id}`));
    const targets: Array<{ invoice: Invoice; lineItems: any[] }> = [];
    for (const invoice of invoices) {
      if ((invoice?.status || '').toString().toLowerCase() !== 'balanced') continue;
      const lineItems = (invoice.lineItem || []).filter((lineItem: any) => {
        const reference = (lineItem?.chargeItemReference?.reference || '').toString();
        return chargeItemReferences.has(reference) || [...chargeItemReferences].some((target) => reference.endsWith(target));
      });
      if (lineItems.length) targets.push({ invoice: structuredClone(invoice), lineItems: [] });
    }
    if (!targets.length) return { invoices: [], professionalCost };

    const totalLineItems = targets.reduce((count, target) => count + (target.invoice.lineItem || []).filter((lineItem: any) => {
      const reference = (lineItem?.chargeItemReference?.reference || '').toString();
      return chargeItemReferences.has(reference) || [...chargeItemReferences].some((item) => reference.endsWith(item));
    }).length, 0);
    if (!totalLineItems) return { invoices: [], professionalCost };

    const estimatedShortageCost = reconciliations.reduce((sum, item) => sum + item.estimatedShortageCost, 0);
    const allocatedCost = Number(((totalConsumedCost + estimatedShortageCost + professionalCost) / totalLineItems).toFixed(2));
    for (const target of targets) {
      for (const lineItem of target.invoice.lineItem || []) {
        const reference = (lineItem?.chargeItemReference?.reference || '').toString();
        if (!chargeItemReferences.has(reference) && ![...chargeItemReferences].some((item) => reference.endsWith(item))) continue;
        lineItem.priceComponent = (lineItem.priceComponent || []).filter((component: any) =>
          component?.type !== 'informational' || component?.code?.text !== 'Cost Price Snapshot'
        );
        lineItem.priceComponent.push({
          type: 'informational',
          code: { text: 'Cost Price Snapshot' },
          amount: { value: allocatedCost, currency: 'NGN' }
        });
        this.replaceInventoryAuditExtensions(lineItem, reconciliations);
      }
    }
    return { invoices: targets.map((target) => target.invoice), professionalCost };
  }

  private replaceInventoryAuditExtensions(lineItem: any, reconciliations: InventoryCostReconciliation[]): void {
    const url = `${this.backend.replace(/\/+$/, '')}/StructureDefinition/inventory-cost-reconciliation`;
    const existing = Array.isArray(lineItem.extension) ? lineItem.extension : [];
    lineItem.extension = existing.filter((extension: any) => extension?.url !== url);
    for (const item of reconciliations) {
      lineItem.extension.push({
        url,
        extension: [
          { url: 'material', valueCodeableConcept: item.material },
          { url: 'requiredQuantity', valueQuantity: { value: item.requiredQuantity, ...(item.unit ? { unit: item.unit } : {}) } },
          { url: 'recordedQuantityUsed', valueQuantity: { value: item.recordedQuantityUsed, ...(item.unit ? { unit: item.unit } : {}) } },
          { url: 'shortageQuantity', valueQuantity: { value: item.shortageQuantity, ...(item.unit ? { unit: item.unit } : {}) } },
          { url: 'recordedBatchCost', valueMoney: { value: Number(item.recordedBatchCost.toFixed(2)), currency: 'NGN' } },
          { url: 'estimatedUnitCost', valueMoney: { value: Number(item.estimatedUnitCost.toFixed(2)), currency: 'NGN' } },
          { url: 'estimatedShortageCost', valueMoney: { value: Number(item.estimatedShortageCost.toFixed(2)), currency: 'NGN' } },
          { url: 'pricingBasis', valueString: item.pricingBasis },
          ...(item.sourceBatchId ? [{ url: 'sourceBatch', valueReference: { reference: `SupplyDelivery/${item.sourceBatchId}` } }] : [])
        ]
      });
    }
  }

  private findItemForKey(rows: ItemsRequiredRow[], key: string, keyOf: (item: any) => string): any {
    return (rows || []).map((row) => row?.items).find((item) => keyOf(item) === key) || null;
  }

  private getRemaining(delivery: any): { value: number; unit?: string } {
    const ext = (delivery?.extension || []).find((entry: any) => typeof entry?.url === 'string' && entry.url.endsWith('/totalRemaining'));
    return { value: Number(ext?.valueQuantity?.value ?? ext?.valueInteger ?? 0), unit: ext?.valueQuantity?.unit };
  }

  private setRemaining(delivery: any, value: number, unit?: string): void {
    if (!Array.isArray(delivery.extension)) delivery.extension = [];
    let ext = delivery.extension.find((entry: any) => typeof entry?.url === 'string' && entry.url.endsWith('/totalRemaining'));
    if (!ext) {
      ext = { url: 'http://hl7.org/fhir/StructureDefinition/totalRemaining' };
      delivery.extension.push(ext);
    }
    ext.valueQuantity = { value: Math.max(0, value), ...(unit ? { unit } : {}) };
    delete ext.valueInteger;
  }

  private unitCost(delivery: any): number {
    const extensions = delivery?.extension || [];
    const totalCost = extensions.find((entry: any) => typeof entry?.url === 'string' && entry.url.endsWith('/totalCostPrice'));
    const totalAmount = extensions.find((entry: any) => typeof entry?.url === 'string' && entry.url.endsWith('/totalAmount'));
    const cost = Number(totalCost?.valueMoney?.value ?? 0);
    const quantity = Number(totalAmount?.valueQuantity?.value ?? 0);
    return quantity > 0 ? cost / quantity : 0;
  }

  private idFromCanonical(canonical: string): string | null {
    const match = (canonical || '').match(/ActivityDefinition\/([^\/#?]+)/i);
    return match?.[1] || null;
  }

  private toCanonical(definition: any): string | null {
    return definition?.id ? `${this.backend.replace(/\/+$/, '')}/ActivityDefinition/${definition.id}` : null;
  }

  private dateValue(resource: any): number {
    return new Date(resource?.occurrenceDateTime || resource?.occurrencePeriod?.start || resource?.meta?.lastUpdated || 0).getTime();
  }
}
